import "../css/GoogleReviews.css";
import useContent from "../../hooks/useContent";
import reviewsSeed from "../data/reviews";

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/Alpha+1+Industrial+Services/@49.5889014,-121.324693,8z/data=!4m8!3m7!1s0x6c5faeb0a0d6433f:0x55b89545f4f6132f!8m2!3d49.5889014!4d-121.324693!9m1!1b1!16s%2Fg%2F11wxlxhc_6";

const AVATAR_TONES = ["#0c0c0d", "#d61f16", "#3d434f"];

// Official Google "G" mark, drawn inline (no external image requests).
const GoogleG = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

const Stars = ({ value, className }) => (
  <span
    className={className ? `gRevStars ${className}` : "gRevStars"}
    role="img"
    aria-label={`Rated ${value} out of 5 stars`}
  >
    {[1, 2, 3, 4, 5].map((i) => (
      <svg
        key={i}
        className={i <= value ? "gRevStar" : "gRevStar isEmpty"}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ))}
  </span>
);

const GoogleReviews = ({ items, href = GOOGLE_REVIEWS_URL }) => {
  const liveItems = useContent("reviews", reviewsSeed);
  const source = items && items.length > 0 ? items : liveItems;
  // Reviews hidden in /admin must not appear here. The test is `!== false`,
  // not `=== true`: reviews saved before the visibility switch existed have no
  // `visible` field at all, and `=== true` would empty the homepage strip.
  const reviews = source.filter((r) => r.visible !== false);

  const count = reviews.length;
  const average = count
    ? reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / count
    : 0;
  const averageLabel = (Math.round(average * 10) / 10).toFixed(1);

  return (
    <section className="gRev">
      <div className="gRevInner">
        <div className="gRevHead">
          <div className="gRevHeadText">
            <span className="gRevKicker">What our customers say</span>
            <h2 className="gRevTitle">Trusted across the Lower Mainland</h2>
          </div>

          <div className="gRevHeadRight">
            <div className="gRevScore">
              <GoogleG className="gRevG" />
              <span className="gRevScoreNum">{averageLabel}</span>
              <span className="gRevScoreDetail">
                <Stars value={Math.round(average)} />
                <span className="gRevScoreMeta">
                  Google Reviews &middot; {count} review{count === 1 ? "" : "s"}
                </span>
              </span>
            </div>
            <a
              className="gRevAll"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              See all reviews on Google
              <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
        </div>

        <ul className="gRevGrid">
          {reviews.map((review, idx) => {
            const name = review.name || "Anonymous";
            const tone =
              AVATAR_TONES[(name.charCodeAt(0) + idx) % AVATAR_TONES.length];
            return (
              <li className="gRevCard" key={review.id || idx}>
                <div className="gRevCardTop">
                  <span
                    className="gRevAvatar"
                    style={{ backgroundColor: tone }}
                    aria-hidden="true"
                  >
                    {name.trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="gRevWho">
                    <span className="gRevName">{name}</span>
                    <span className="gRevDate">{review.date}</span>
                  </span>
                  <GoogleG className="gRevBadge" />
                </div>
                <Stars value={Number(review.rating) || 0} />
                <p className="gRevText">{review.text}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default GoogleReviews;
